import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Stack, IconButton, Divider, Box, Typography, Grid, TextField, Autocomplete, MenuItem, FormControl, FormControlLabel, Button, Select, Switch, Snackbar, Alert, Card, Chip, Avatar, Tooltip, tooltipClasses } from '@mui/material';
import { ArrowBack, History, DescriptionOutlined, Person, Apartment, InfoOutlined, Check, Close } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO, format, isSameDay, formatISO } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getData, postData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import { styled } from '@mui/material/styles';
import { getStatusColor, priorityColors } from '../../../utils/statusColor';
import Loader from '../../../common/spinner';
import { formatDate } from '../../../utils/dateFormat';

const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#f5f5f9',
        color: 'rgba(0, 0, 0, 0.87)',
        maxWidth: 220,
        fontSize: theme.typography.pxToRem(12), // Now theme will be defined
        border: '1px solid #dadde9',
        padding: '10px'
    },
}));

const BarSegment = styled(Box)(({ color, width }) => ({
    backgroundColor: color,
    width: `${width}%`,
    height: '100%',
    transition: 'width 0.5s ease-in-out',
}));

// Defined outside EditProjectPage so identity stays stable across renders —
// otherwise React remounts the underlying inputs on every keystroke, dropping focus.
const StatusBadge = ({ type, value }) => {
    let style = { bg: "#ECEFF1", color: "#37474F" }; // Fallback

    if (type === 'status') {
        style = getStatusColor(value);
    } else if (type === 'priority') {
        style = priorityColors[value] || style;
    }

    return (
        <Box sx={{
            bgcolor: style.bg,
            color: style.color,
            border: `1px solid ${style.border}`,
            px: 2,
            py: 0.5,
            borderRadius: '25px',
            fontSize: '0.85rem',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center'
        }}>
            {value}
        </Box>
    );
};

const DataField = ({ label, value, isEditingField, type, tooltip, children, isEditing }) => (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {label}
            </Typography>
            {tooltip && (
                <Tooltip title={tooltip} arrow placement="top">
                    <InfoOutlined sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help' }} />
                </Tooltip>
            )}
        </Stack>
        <Box>
            {isEditing && isEditingField ? children : (
                <Box sx={{ minHeight: '1.5rem', display: 'flex', alignItems: 'center' }}>
                    {type === 'status' || type === 'priority' ? (
                        <StatusBadge type={type} value={value} />
                    ) : (
                        <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                            {value || '-'}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    </Grid>
);

const EditProjectPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const projectId = useMemo(() => searchParams.get("projectId"), [searchParams]);

    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [projectData, setProjectData] = useState(null);
    const [options, setOptions] = useState({ leads: [], managers: [] });
    const [form, setForm] = useState({
        project_title: '',
        client: '',
        projectManager: null,
        teamLead: null,
        startDate: null,
        endDate: null,
        priority: '',
        status: '',
        description: '',
        reason_for_change: '',
        progresspercentage: '0',
        date_history: [],
        enable_two_level_timesheet_approval: false,
        manager_can_edit_timesheet: true,
        external_can_edit_timesheet: false,
        manager_auto_approve_timesheet: false,
        external_approver_name: '',
    });
    const [initial, setInitial] = useState({});
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [externalApproverOptions, setExternalApproverOptions] = useState([]);
    const [selectedExternalApprover, setSelectedExternalApprover] = useState(null);
    const [globalConfig, setGlobalConfig] = useState(null);

    const isEndDateChanged = useMemo(() => {
        if (!form.endDate || !initial.endDate) return false;
        return !isSameDay(new Date(form.endDate), new Date(initial.endDate));
    }, [form.endDate, initial.endDate]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const fetchAllData = useCallback(async (projectDetails) => {
        try {
            const userDataRaw = localStorage.getItem('userData');
            const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
            const orgId = userData?.organization_id;

            const [leads, managers] = await Promise.all([
                getData(API_ENDPOINTS.fetchTeamLeadsOrManagers(orgId, 'is_team_lead')),
                getData(API_ENDPOINTS.fetchTeamLeadsOrManagers(orgId, 'is_manager'))
            ]);

            const d = projectDetails;
            const mapUser = (u) => u ? { id: u.id, name: `${u.first_name || ''} ${u.last_name || ''}`.trim() } : null;

            const leadOpts = (leads?.message?.users || []).map(mapUser);
            const mgrOpts = (managers?.message?.users || []).map(mapUser);

            const init = {
                project_title: d?.project?.project_title || '',
                priority: d?.project?.priority || 'Low',
                status: d?.project?.status || 'Todo',
                startDate: d?.project?.start_date ? parseISO(d.project.start_date) : null,
                endDate: d?.project?.end_date ? parseISO(d.project.end_date) : null,
                projectManager: mgrOpts.find(o => o.id === d?.manager?.id) || null,
                teamLead: leadOpts.find(o => o.id === d?.team_leader?.id) || null,
                description: d?.project?.description || '',
                client: d?.clients?.[0] ? `${d.clients[0].first_name} ${d.clients[0].last_name}` : '',
                reason_for_change: '',
                progresspercentage: d?.project?.progress?.toString() || '0',
                date_history: d?.date_history || [],
                total_working_days: d?.project?.total_working_days || 0,
                total_working_hours: d?.project?.total_working_hours || 0,
                departments: d?.departments || [],
                enable_two_level_timesheet_approval: d?.project?.enable_two_level_timesheet_approval || false,
                manager_can_edit_timesheet: d?.project?.manager_can_edit_timesheet ?? true,
                external_can_edit_timesheet: d?.project?.external_can_edit_timesheet || false,
                manager_auto_approve_timesheet: d?.project?.manager_auto_approve_timesheet || false,
                external_approver_name: '',
            };

            setOptions({ leads: leadOpts, managers: mgrOpts });
            setForm(init);
            setInitial(init);
        } catch (e) {
            setSnackbar({ open: true, message: 'Failed to load necessary data', severity: 'error' });
        }
    }, []);

    const fetchProjectDetailsById = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.fetchProjectById(projectId));
            if (res.status === "SUCCESS") {
                setProjectData(res.message.data);
                await fetchAllData(res.message.data);
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to load project details', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [projectId, fetchAllData]);

    useEffect(() => {
        if (projectId) fetchProjectDetailsById();
    }, [projectId, fetchProjectDetailsById]);

    useEffect(() => {
        const fetchExternalApprovers = async () => {
            const raw = localStorage.getItem('userData');
            const parsed = raw ? JSON.parse(raw) : null;
            const orgId = parsed?.organization_id;
            if (!orgId) return;
            try {
                const [approversRes, cfgRes] = await Promise.all([
                    getData(API_ENDPOINTS.getExternalApprovers(orgId)),
                    getData(API_ENDPOINTS.getTimesheetConfig()),
                ]);
                if (approversRes?.status === "SUCCESS") {
                    setExternalApproverOptions(
                        (approversRes.message?.users || []).map(u => ({
                            id: u.id,
                            name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                        }))
                    );
                }
                const cfg = cfgRes?.result?.data ?? cfgRes?.result ?? null;
                setGlobalConfig(cfg);
            } catch {
                setExternalApproverOptions([]);
            }
        };
        fetchExternalApprovers();
    }, []);

    useEffect(() => {
        if (externalApproverOptions.length > 0 && projectData) {
            const approverId = projectData?.project?.external_approver_id;
            setSelectedExternalApprover(
                approverId ? externalApproverOptions.find(o => o.id === approverId) || null : null
            );
        }
    }, [externalApproverOptions, projectData]);

    const handleSave = async () => {
        let finalErrors = {};
        if (!form.project_title) finalErrors.project_title = "Project Name is required";
        if (!form.client) finalErrors.client = "Client is required";
        if (!form.projectManager) finalErrors.projectManager = "Manager is required";
        if (!form.teamLead) finalErrors.teamLead = "Team Lead is required";
        if (!form.endDate) finalErrors.endDate = "End Date is mandatory";

        if (isEndDateChanged && !form.reason_for_change) {
            finalErrors.reason_for_change = "Reason is required for date changes";
        }

        if (Object.keys(finalErrors).length > 0) {
            setErrors(finalErrors);
            setSnackbar({ open: true, message: 'Please fix validation errors', severity: 'error' });
            return;
        }

        const payload = Object.entries(form).reduce((acc, [key, val]) => {
            const isChanged = JSON.stringify(val) !== JSON.stringify(initial[key]);
            if (!isChanged && key !== 'reason_for_change') return acc;

            if (key === 'startDate' || key === 'endDate') {
                acc[key === 'startDate' ? 'start_date' : 'end_date'] = val ? formatISO(val) : null;
            } else if (key === 'projectManager') {
                acc.manager_id = val?.id || null;
            } else if (key === 'teamLead') {
                acc.team_leader_id = val?.id || null;
            } else if (key === 'progresspercentage') {
                acc.progress = parseInt(val, 10);
            } else if (key === 'client') {
                const [f, ...l] = val.trim().split(' ');
                acc.clients = [{ first_name: f, last_name: l.join(' ') }];
            } else if (key === 'reason_for_change') {
                if (isEndDateChanged) acc.reason = val;
            } else if (key !== 'date_history') {
                acc[key] = val;
            }
            return acc;
        }, { project_id: projectId });

        const currentApproverId = selectedExternalApprover?.id || null;
        const initialApproverId = projectData?.project?.external_approver_id || null;
        if (currentApproverId !== initialApproverId) {
            payload.external_approver_id = currentApproverId;
        }

        try {
            const res = await postData(API_ENDPOINTS.updateProject, payload);
            if (res?.status === 'SUCCESS') {
                setSnackbar({ open: true, message: 'Project updated successfully!', severity: 'success' });
                setIsEditing(false);
                setInitial(form);
                setErrors({});
                fetchProjectDetailsById();
            }
        } catch (e) {
            setSnackbar({ open: true, message: e.response?.data?.message || 'Update failed', severity: 'error' });
        }
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" alignItems="center" height="50vh"><Loader /></Box>;
    }

    const { project } = projectData || {};

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            {/* Header Section */}
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', mb: 3, py: 1 }}>
                <Tooltip title="Back">
                    <IconButton onClick={() => navigate(-1)} sx={{ color: '#64748b', borderRadius: '0px', p: 1 }}>
                        <ArrowBack sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 40, alignSelf: 'center' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight="bold" title={project?.project_title} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project?.project_title}</Typography>
                        <Typography variant="body2" color="#64748b">ID: #{projectData?.project?.project_id}</Typography>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                        {!isEditing ? (
                            <Button variant="contained" size="small" onClick={() => setIsEditing(true)} sx={{ bgcolor: '#6366f1', textTransform: 'none', borderRadius: '8px' }}>
                                Edit Project
                            </Button>
                        ) : (
                            <>
                                <Tooltip title="Save Changes">
                                    <IconButton size="small" onClick={handleSave} sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: '8px', '&:hover': { bgcolor: '#c8e6c9' } }}>
                                        <Check fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                    <IconButton size="small" onClick={() => { setIsEditing(false); setForm(initial); setErrors({}); const approverId = projectData?.project?.external_approver_id; setSelectedExternalApprover(approverId ? externalApproverOptions.find(o => o.id === approverId) || null : null); }} sx={{ bgcolor: '#ffebee', color: '#c62828', borderRadius: '8px', '&:hover': { bgcolor: '#ffcdd2' } }}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Stack>
                </Box>
            </Stack>

            <Box>
                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionOutlined color="primary" /> Project Information
                        </Typography>
                        <Grid container spacing={3}>
                            <DataField label="Project Name" value={form.project_title} isEditingField isEditing={isEditing}>
                                <TextField fullWidth error={!!errors.project_title} helperText={errors.project_title}
                                    value={form.project_title} size="small" onChange={e => handleChange('project_title', e.target.value)} />
                            </DataField>

                            <DataField label="Client" value={form.client} isEditingField isEditing={isEditing}>
                                <TextField fullWidth error={!!errors.client} helperText={errors.client}
                                    value={form.client} size="small" onChange={e => handleChange('client', e.target.value)} />
                            </DataField>

                            <DataField label="Manager" value={form.projectManager?.name || 'N/A'} isEditingField isEditing={isEditing}>
                                <Autocomplete options={options.managers} getOptionLabel={o => o.name} value={form.projectManager} size="small" isOptionEqualToValue={(o, v) => o.id === v.id}
                                    onChange={(_, v) => handleChange('projectManager', v)} renderInput={p => <TextField {...p} error={!!errors.projectManager} helperText={errors.projectManager} />} />
                            </DataField>

                            <DataField label="Team Lead" value={form.teamLead?.name || 'N/A'} isEditingField isEditing={isEditing}>
                                <Autocomplete options={options.leads} getOptionLabel={o => o.name} value={form.teamLead} size="small" isOptionEqualToValue={(o, v) => o.id === v.id}
                                    onChange={(_, v) => handleChange('teamLead', v)} renderInput={p => <TextField {...p} error={!!errors.teamLead} helperText={errors.teamLead} />} />
                            </DataField>

                            <DataField label="Start Date" value={form.startDate && format(form.startDate, 'PPP')} isEditingField isEditing={isEditing}>
                                <DatePicker value={form.startDate} disabled format='dd/MM/yyyy' sx={{ width: '100%' }} slotProps={{ textField: { size: 'small' } }} />
                            </DataField>

                            <DataField label="End Date" value={form.endDate && format(form.endDate, 'PPP')} isEditingField isEditing={isEditing}>
                                <DatePicker
                                    value={form.endDate}
                                    minDate={form.startDate || new Date()}
                                    format='dd/MM/yyyy'
                                    onChange={v => handleChange('endDate', v)}
                                    sx={{ width: '100%' }}
                                    slotProps={{ textField: { size: 'small', error: !!errors.endDate, helperText: errors.endDate } }}
                                />
                            </DataField>

                            {/* <DataField
                                label="Total Working Days"
                                value={`${form.total_working_days} Days`}
                                tooltip={`Calculation: ${projectData?.project?.total_working_days || 0} Working Days Ã— 8 Hours/Day`}
                            />

                            <DataField
                                label="Total Working Hours"
                                value={`${form.total_working_hours} Hrs`}
                                tooltip={`Total Hours = Working Days (${projectData?.project?.total_working_days || 0}) * Working Hours per Day (8)`}
                            /> */}

                            <DataField label="Priority" value={form.priority} type="priority" isEditingField isEditing={isEditing}>
                                <FormControl fullWidth size="small">
                                    <Select value={form.priority} onChange={(e) => handleChange('priority', e.target.value)}>
                                        <MenuItem value="High">High</MenuItem>
                                        <MenuItem value="Medium">Medium</MenuItem>
                                        <MenuItem value="Low">Low</MenuItem>
                                    </Select>
                                </FormControl>
                            </DataField>

                            <DataField label="Status" value={form.status} type="status" isEditingField isEditing={isEditing}>
                                <FormControl fullWidth size="small">
                                    <Select value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                                        <MenuItem value="Todo">Todo</MenuItem>
                                        <MenuItem value="In Progress">In Progress</MenuItem>
                                        <MenuItem value="Completed">Completed</MenuItem>
                                    </Select>
                                </FormControl>
                            </DataField>

                            {/* Timesheet Approval Settings — only shown when HR config scope is PROJECT AND two-level approval is enabled */}
                            {globalConfig?.approval_scope === 'PROJECT' && globalConfig?.is_two_level_approval && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
                                        Timesheet Approval Settings
                                    </Typography>
                                    {isEditing ? (
                                        <Box sx={{ borderRadius: 2, border: '1px solid #e0e7ff', bgcolor: '#f8f9ff', overflow: 'hidden' }}>
                                            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #e0e7ff', bgcolor: '#eef2ff' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#4338ca' }}>Approval Configuration</Typography>
                                            </Box>
                                            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {/* Two-Level Toggle */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>Enable Two-Level Approval</Typography>
                                                        <Typography variant="caption" sx={{ color: '#64748b' }}>Requires Manager + External Approver sign-off</Typography>
                                                    </Box>
                                                    <Switch
                                                        checked={form.enable_two_level_timesheet_approval}
                                                        onChange={(e) => { handleChange('enable_two_level_timesheet_approval', e.target.checked); if (!e.target.checked) { handleChange('external_can_edit_timesheet', false); handleChange('external_approver_name', ''); setSelectedExternalApprover(null); } }}
                                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' } }}
                                                    />
                                                </Box>

                                                {/* Manager Can Edit — fully manager-controlled at the project level in PROJECT scope */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                            Allow Manager to Edit Entries
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                            Manager can modify hours and remarks before approving
                                                        </Typography>
                                                    </Box>
                                                    <Switch
                                                        checked={form.manager_can_edit_timesheet ?? true}
                                                        onChange={(e) => handleChange('manager_can_edit_timesheet', e.target.checked)}
                                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' } }}
                                                    />
                                                </Box>

                                                {/* External Can Edit — only when two-level; fully manager-controlled at the project level in PROJECT scope */}
                                                {form.enable_two_level_timesheet_approval && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                                Allow External Approver to Edit Entries
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                                External approver can modify limited fields
                                                            </Typography>
                                                        </Box>
                                                        <Switch
                                                            checked={form.external_can_edit_timesheet ?? false}
                                                            onChange={(e) => handleChange('external_can_edit_timesheet', e.target.checked)}
                                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' } }}
                                                        />
                                                    </Box>
                                                )}

                                                {/* Auto-Approve Manager Review — only when two-level; skips this project's manager sign-off once the external approver clears it */}
                                                {form.enable_two_level_timesheet_approval && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                                Auto-Approve Manager Review
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                                Skip manager sign-off for this project once the external approver approves
                                                            </Typography>
                                                        </Box>
                                                        <Switch
                                                            checked={form.manager_auto_approve_timesheet ?? false}
                                                            onChange={(e) => handleChange('manager_auto_approve_timesheet', e.target.checked)}
                                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' } }}
                                                        />
                                                    </Box>
                                                )}

                                                {/* External Approver Dropdown */}
                                                {form.enable_two_level_timesheet_approval && (
                                                    <Autocomplete
                                                        options={externalApproverOptions}
                                                        getOptionLabel={(option) => option.name}
                                                        value={selectedExternalApprover}
                                                        onChange={(_, val) => setSelectedExternalApprover(val)}
                                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                                        renderInput={(params) => (
                                                            <TextField {...params} size="small" fullWidth label="External Approver" placeholder="Select external approver" />
                                                        )}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: form.enable_two_level_timesheet_approval ? '#6366f1' : '#cbd5e1' }} />
                                                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                        Two-Level: {form.enable_two_level_timesheet_approval ? 'Enabled' : 'Disabled'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: (form.manager_can_edit_timesheet ?? true) ? '#10b981' : '#cbd5e1' }} />
                                                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                        Manager Edit: {(form.manager_can_edit_timesheet ?? true) ? 'Allowed' : 'Not Allowed'}
                                                    </Typography>
                                                </Box>
                                                {form.enable_two_level_timesheet_approval && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: form.external_can_edit_timesheet ? '#10b981' : '#cbd5e1' }} />
                                                        <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                            External Edit: {form.external_can_edit_timesheet ? 'Allowed' : 'Not Allowed'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {form.enable_two_level_timesheet_approval && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: form.manager_auto_approve_timesheet ? '#6366f1' : '#cbd5e1' }} />
                                                        <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                            Manager Auto-Approve: {form.manager_auto_approve_timesheet ? 'Enabled' : 'Disabled'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            {form.enable_two_level_timesheet_approval && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: selectedExternalApprover ? '#6366f1' : '#cbd5e1' }} />
                                                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                        Approver: {selectedExternalApprover ? selectedExternalApprover.name : 'Not Assigned'}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Grid>
                            )}

                            {isEndDateChanged && isEditing && (
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Reason for Date Change *"
                                        value={form.reason_for_change}
                                        error={!!errors.reason_for_change}
                                        helperText={errors.reason_for_change}
                                        onChange={e => handleChange('reason_for_change', e.target.value)}
                                    />
                                </Grid>
                            )}

                            <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                    Description
                                </Typography>
                                {isEditing ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={6}
                                        value={form.description}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                    />
                                ) : (
                                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#334155',
                                                lineHeight: 1.6,
                                                width: '100%',
                                                display: 'block',        // Ensures it takes full width
                                                whiteSpace: 'pre-wrap',  // Preserves line breaks and wraps text
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {form.description || 'No description provided.'}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Card variant="outlined" sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', gap: 1, borderBottom: '1px solid #e2e8f0' }}>
                                        <History sx={{ color: '#64748b' }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Timeline Adjustment History
                                        </Typography>
                                    </Box>

                                    <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                                        {form?.date_history?.length > 0 ? (
                                            <Stack spacing={0} divider={<Divider />}>
                                                {form.date_history.map((item, i) => (
                                                    <Box key={i} sx={{ p: 2.5 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                                                            <Stack spacing={0.5}>
                                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                                                                    {item.createdAt ? format(parseISO(item.createdAt), 'PPp') : 'N/A'}
                                                                </Typography>
                                                                {/* Displaying the user who made the change */}
                                                                {item.changedBy && (
                                                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                        <Avatar sx={{ width: 18, height: 18, fontSize: '10px', bgcolor: '#6366f1' }}>
                                                                            {item.changedBy.first_name?.[0]}
                                                                        </Avatar>
                                                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                                                                            Modified by <b>{item.changedBy.first_name} {item.changedBy.last_name}</b>
                                                                        </Typography>
                                                                    </Stack>
                                                                )}
                                                            </Stack>

                                                            <Stack direction="row" spacing={1}>
                                                                <Box sx={{ px: 1, py: 0.5, bgcolor: '#eef2ff', borderRadius: 1, border: '1px solid #e0e7ff' }}>
                                                                    <Typography variant="caption" sx={{ color: '#4338ca', fontWeight: 700 }}>
                                                                        Start: {formatDate(item.modified_start_date)}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ px: 1, py: 0.5, bgcolor: '#fff1f2', borderRadius: 1, border: '1px solid #ffe4e6' }}>
                                                                    <Typography variant="caption" sx={{ color: '#be123c', fontWeight: 700 }}>
                                                                        End: {formatDate(item.modified_end_date)}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        </Box>
                                                        <Typography variant="body2" sx={{ color: '#475569', fontStyle: 'italic', bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, wordBreak: 'break-word' }}>
                                                            {item.reason}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        ) : (
                                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">No date changes recorded yet.</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card variant="outlined" sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 2, mb: 3, bgcolor: '#f8fafc' }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                Project Calendar Summary
                            </Typography>
                            <Stack spacing={1}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" color="text.secondary">Total Days</Typography>
                                        <Tooltip title="Total number of days from start date to end date" arrow placement="top">
                                            <InfoOutlined sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                    <Typography variant="body2" fontWeight={700}>{projectData?.project_summary?.total_days ?? 0}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" color="text.secondary">Total Holidays</Typography>
                                        <Tooltip title="Number of holidays within the project duration" arrow placement="top">
                                            <InfoOutlined sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                    <Typography variant="body2" fontWeight={700}>{projectData?.project_summary?.total_holidays ?? 0}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" color="text.secondary">Total Weekends</Typography>
                                        <Tooltip title="Number of weekend days (Saturday and Sunday) within the project duration" arrow placement="top">
                                            <InfoOutlined sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                    <Typography variant="body2" fontWeight={700}>{projectData?.project_summary?.total_weekends ?? 0}</Typography>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" color="text.secondary">Working Days</Typography>
                                        <Tooltip title="Total days available for work (Total Days - Holidays - Weekends)" arrow placement="top">
                                            <InfoOutlined sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                    <Typography variant="body2" fontWeight={700}>{projectData?.project_summary?.total_working_days ?? 0}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" color="text.secondary">Working Hours</Typography>
                                        <Tooltip title="Total working hours (Working Days Ã— 8 hours per day)" arrow placement="top">
                                            <InfoOutlined sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                    <Typography variant="body2" fontWeight={700}>{projectData?.project_summary?.total_working_hours ?? 0}</Typography>
                                </Box>
                            </Stack>
                        </Card>

                        <Typography variant="overline" color="text.secondary" fontWeight="700">
                            DEPARTMENTS & WORKSPACE
                        </Typography>

                        {form.departments?.map((dept) => (
                            <Box key={dept.department_id} sx={{ mb: 4, mt: 2 }}>
                                <Typography variant="subtitle1" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#1e293b' }}>
                                    <Apartment sx={{ mr: 1, color: '#2563eb' }} />
                                    {dept.department_name}
                                    <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>
                                        ({dept.teams?.length || 0} {dept.teams?.length === 1 ? 'Team' : 'Teams'})
                                    </Typography>
                                </Typography>

                                {dept.teams?.map((team) => (
                                    <Box key={team.team_id} sx={{ ml: 4, mb: 3 }}>
                                        <Typography variant="body2" color="#475569" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Person sx={{ fontSize: 16, mr: 1, opacity: 0.6 }} /> {team.team_name}
                                        </Typography>

                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {team.members?.map((member) => (
                                                <Chip
                                                    key={member.id}
                                                    size="small"
                                                    avatar={<Avatar>{member.first_name?.[0]}</Avatar>}
                                                    label={`${member.first_name} ${member.last_name}`}
                                                    sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', fontWeight: 500 }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                ))}
                            </Box>
                        ))}

                        {/* Progress Section */}
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="overline" color="text.secondary" fontWeight="700">
                                PROJECT RESOURCE ALLOCATION
                            </Typography>

                            {/* Calculation of percentages based on allocated hours */}
                            {(() => {
                                const total = projectData?.project?.total_working_hours || 1; // Avoid division by zero
                                const summary = projectData?.summary || {};

                                const epicP = (summary.epics?.allocated / total) * 100;
                                const storyP = (summary.user_stories?.allocated / total) * 100;
                                const taskP = (summary.tasks?.allocated / total) * 100;
                                const subP = (summary.sub_tasks?.allocated / total) * 100;
                                // const remainingP = 100 - (epicP + storyP + taskP + subP);

                                return (
                                    <HtmlTooltip
                                        title={
                                            <React.Fragment>
                                                <Typography color="inherit" variant="subtitle2" fontWeight="bold">Project Hours Breakdown</Typography>
                                                <Divider sx={{ my: 1 }} />
                                                <Stack spacing={0.5}>
                                                    <Box display="flex" justifyContent="space-between">
                                                        <Typography variant="caption">Total Project:</Typography>
                                                        <Typography variant="caption" fontWeight="bold">{summary?.project_total || 0} Hrs</Typography>
                                                    </Box>
                                                    <Box display="flex" justifyContent="space-between" color="#ffc107">
                                                        <Typography variant="caption">Epic Hours:</Typography>
                                                        <Typography variant="caption" fontWeight="bold">{summary.epics?.allocated || 0} Hrs</Typography>
                                                    </Box>
                                                    <Box display="flex" justifyContent="space-between" color="#4caf50">
                                                        <Typography variant="caption">Story Hours:</Typography>
                                                        <Typography variant="caption" fontWeight="bold">{summary.user_stories?.allocated || 0} Hrs</Typography>
                                                    </Box>
                                                    <Box display="flex" justifyContent="space-between" color="#6366f1">
                                                        <Typography variant="caption">Task Hours:</Typography>
                                                        <Typography variant="caption" fontWeight="bold">{summary.tasks?.allocated || 0} Hrs</Typography>
                                                    </Box>
                                                    <Box display="flex" justifyContent="space-between" color="#f44336">
                                                        <Typography variant="caption">Sub-Task Hours:</Typography>
                                                        <Typography variant="caption" fontWeight="bold">{summary.sub_tasks?.allocated || 0} Hrs</Typography>
                                                    </Box>
                                                </Stack>
                                            </React.Fragment>
                                        }
                                    >
                                        <Box sx={{
                                            height: 10,
                                            width: '100%',
                                            bgcolor: '#e2e8f0',
                                            borderRadius: 5,
                                            display: 'flex',
                                            overflow: 'hidden',
                                            mt: 1,
                                            cursor: 'pointer'
                                        }}>
                                            <BarSegment color="#ffc107" width={epicP} />   {/* Yellow for Epics */}
                                            <BarSegment color="#4caf50" width={storyP} />  {/* Green for Stories */}
                                            <BarSegment color="#6366f1" width={taskP} />   {/* Indigo for Tasks */}
                                            <BarSegment color="#f44336" width={subP} />    {/* Red for Subtasks */}
                                        </Box>
                                    </HtmlTooltip>
                                );
                            })()}

                            <Box display="flex" justifyContent="space-between" mt={1}>
                                <Typography variant="caption" color="text.secondary">
                                    Used: {projectData?.summary?.project_total?.used || 0} Hrs
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Remaining: {projectData?.summary?.project_total} Hrs
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </LocalizationProvider>
    );
};

export default EditProjectPage;
